import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export type ConversationListItem = {
  id: string
  other_user_id: string
  other_user_username: string
  other_user_first_name: string
  other_user_last_name: string
  last_message_at: string | null
  last_message_preview: string | null
  last_message_sender_id: string | null
  unread_count: number
}

export function useConversations() {
  return useQuery({
    queryKey: ['conversations'],
    refetchInterval: 8000, // poll every 8 seconds
    queryFn: async (): Promise<ConversationListItem[]> => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) return []

      // RLS filters out conversations where blocking exists
      const { data: convs, error } = await supabase
        .from('conversations')
        .select(
          'id, user_a, user_b, last_message_at, last_message_preview, last_message_sender_id, user_a_unread_count, user_b_unread_count'
        )
        .order('last_message_at', { ascending: false, nullsFirst: false })

      if (error) throw error
      if (!convs?.length) return []

      // Collect all other-user IDs for a single bulk RPC call (bypasses RLS)
      const otherIds = convs.map((c) => (c.user_a === user.id ? c.user_b : c.user_a))

      const { data: profilesData } = await supabase.rpc(
        'get_resident_public_profiles',
        { _user_ids: otherIds }
      )

      const residentMap = new Map(
        ((profilesData as any[]) ?? []).map((r: any) => [r.id, r])
      )

      return convs.map((c) => {
        const otherUserId = c.user_a === user.id ? c.user_b : c.user_a
        const unreadCount =
          c.user_a === user.id ? c.user_a_unread_count : c.user_b_unread_count
        const resident = residentMap.get(otherUserId)

        return {
          id: c.id,
          other_user_id: otherUserId,
          other_user_username: resident?.username ?? 'unknown',
          other_user_first_name: resident?.first_name ?? '',
          other_user_last_name: resident?.last_name ?? '',
          last_message_at: c.last_message_at,
          last_message_preview: c.last_message_preview,
          last_message_sender_id: c.last_message_sender_id,
          unread_count: unreadCount ?? 0,
        }
      })
    },
  })
}
